import React, { useEffect } from "react";
import CustomInput from "../../../../components/common/CustomInput/CustomInput";
import CustomFileUpload from "../../../../components/common/CustomFileUpload/CustomFileUpload";
import BodyHeader from "../../../../components/common/CommonBodyHeader";
import CustomTextArea from "../../../../components/common/CustomTextArea/CustomTextArea";
import { FormikProvider, Form, useFormik, FieldArray } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { backdropLoadingAction } from "../../../../redux/features/helperSlice";
import { useNavigate } from "react-router-dom";
import { getFleetMasterDropdownDataAsync } from "../../../../redux/features/fleetMaster";
import CustomDropdown from "../../../../components/common/CustomDropdown/CustomDropdown";
import toast from "react-hot-toast";
import validation from "../../../../helpers/validation";
import { addContractorMasterAsync } from "../../../../redux/features/contractorMasterSlice";
import { MAX_DATE } from "../../../../Hooks/dateValidationRules";
import {
   isValidFileType,
   MAX_FILE_SIZE,
   MAX_FILE_SIZE_MESSAGE,
} from "../../../../helpers/fileValidationRules";
import CustomDatePicker from "../../../../components/common/CustomDatePicker/CustomDatePicker";

const validationSchema = Yup.object().shape({
   contractorName: validation.name("Contractor Name"),
   location: Yup.string().required("Location is required"),
   gstNumber: validation.gstNumber,
   gstDocument: validation.file("GST Document", true, "all"),
   address: Yup.string().required("Address is required"),

   contactPerson: Yup.array().of(
      Yup.object().shape({
         name: Yup.string().required("Contact Person Name is required"),
         mobile: Yup.string()
            .matches(/^\d{10}$/, "Mobile number must be 10 digits")
            .required("Mobile number is required"),
         email: Yup.string().email("Invalid email address").required("Email is required"),
      })
   ),

   accountNo: validation.accountNumber(),
   bankName: validation.name("Bank Name"),
   ifscCode: validation.ifscCode(),
   accountHolderName: validation.name("Account Holder Name"),
   bankDocument: validation.file("Bank Document"),
   // agreementDocument: Yup.array().of(validation.file("Agreement Document")),
   // agreementDocument: validation.file("Agreement Document", true, "all"),
   agreementDetails: Yup.array().of(
      Yup.object().shape({
         startDate: Yup.date()
            .required("Start Date is required")
            .max(MAX_DATE, "The date cannot be later than December 31, 2999"),
         endDate: Yup.date()
            .required("End Date is required")
            .max(MAX_DATE, "The date cannot be later than December 31, 2999"),
         agreementDocument: Yup.mixed()
            .required("Agreement document is required")
            .test("is-valid-type", "Not a valid document type", (value) => {
               return isValidFileType(value && value.name.toLowerCase(), "all");
            })
            .test(
               "is-valid-size",
               MAX_FILE_SIZE_MESSAGE,
               (value) => value && value.size <= MAX_FILE_SIZE
            ),
      })
   ),
});

const initialValues = {
   contractorName: "",
   location: "",
   gstNumber: "",
   gstDocument: "",
   address: "",

   contactPerson: [{ name: "", mobile: "", email: "" }],

   accountNo: "",
   bankName: "",
   ifscCode: "",
   accountHolderName: "",
   bankDocument: null,

   // agreementDocument: null,
   agreementDetails: [{ startDate: "", endDate: "", agreementDocument: null }],
};

const ContractorMasterAdd = () => {
   const dispatch = useDispatch();
   const navigate = useNavigate();

   const fleetMaster = useSelector((state) => state.fleetMaster);

   const locationData = fleetMaster?.location?.map((item) => ({
      ...item,
      label: item?.name,
      value: item?.id,
   }));

   const handleContractor = async (formData) => {
      const conpername = formData?.contactPerson?.map?.((item, index) => {
         return item.name;
      });

      const conpermobile = formData?.contactPerson?.map?.((item, index) => {
         return item.mobile;
      });

      const conperemail = formData?.contactPerson?.map?.((item, index) => {
         return item.email;
      });

      const agreementStartDate = formData?.agreementDetails?.map?.(
         (item) => item?.startDate
      );
      const agreementEndDate = formData?.agreementDetails?.map?.((item) => item?.endDate);

      const newData = new FormData();

      newData.append("name", formData.contractorName);
      newData.append("location", formData.location);
      newData.append("gst_no", formData.gstNumber);
      newData.append("address", formData.address);

      newData.append("account_no", formData.accountNo);
      newData.append("bank_name", formData.bankName);
      newData.append("ifsc_code", formData.ifscCode);
      newData.append("account_holder_name", formData.accountHolderName);

      newData.append("gst_document", formData.gstDocument);
      newData.append("bank_document", formData.bankDocument);
      // newData.append("agreement_details", formData.agreementDocument);

      formData?.agreementDetails?.forEach?.((item) => {
         newData.append("agreement_document[]", item?.agreementDocument);
      });
      newData.append("start_date[]", agreementStartDate);
      newData.append("end_date[]", agreementEndDate);

      newData.append("contact_person_name[]", conpername);
      newData.append("mobile[]", conpermobile);
      newData.append("email[]", conperemail);
      newData.append("model_id", 9);
      newData.append("action_id", 1);

      dispatch(backdropLoadingAction(true));
      dispatch(
         addContractorMasterAsync({
            data: newData,
            err: () => {
               dispatch(backdropLoadingAction(false));
               toast.error("Something went wrong !");
            },
            done: () => {
               dispatch(backdropLoadingAction(false));
               toast.success("Record added successfully !");
               navigate("/contractor-master/view");
            },
         })
      );
   };

   useEffect(() => {
      dispatch(getFleetMasterDropdownDataAsync());
   }, [dispatch]);

   const formik = useFormik({
      initialValues: initialValues,
      validationSchema: validationSchema,
      onSubmit: handleContractor,
   });

   const {
      errors,
      touched,
      values,
      getFieldProps,
      handleSubmit,
      resetForm,
      setFieldValue,
   } = formik;

   return (
      <div>
         <BodyHeader title="Add Contractor Master" />
         <FormikProvider value={formik}>
            <Form className="p-3 shadow-lg">
               <div className="row">
                  <div className="col-lg-12">
                     <div className="card card-primary">
                        <div className="card-header">
                           <h3 className="card-title">Personal Details</h3>
                        </div>
                        <div className="card-body">
                           <div className="row">
                              <div className="col-12 col-sm-12 col-md-4 col-lg-4">
                                 <CustomInput
                                    require
                                    label="Contractor Name"
                                    id="#name"
                                    name="name"
                                    placeholder="E.g contractor"
                                    {...getFieldProps(`contractorName`)}
                                    errors={
                                       Boolean(touched.contractorName) &&
                                       Boolean(errors.contractorName)
                                    }
                                    message={errors.contractorName}
                                 />
                              </div>

                              <div className="col-12 col-sm-12 col-md-4 col-lg-4">
                                 <CustomDropdown
                                    require
                                    label="Location"
                                    id="#location"
                                    name="location"
                                    optionData={locationData}
                                    placeholder="Enter Location"
                                    // {...getFieldProps(`location`)}
                                    onChange={(values) => {
                                       setFieldValue("location", values?.[0]?.value);
                                    }}
                                    errors={
                                       Boolean(touched.location) &&
                                       Boolean(errors.location)
                                    }
                                    message={errors.location}
                                 />
                              </div>

                              <div className="col-lg-4">
                                 <CustomInput
                                    require
                                    label="GST Number"
                                    id="gst_no"
                                    name="gst_no"
                                    placeholder="Enter GST Number"
                                    {...getFieldProps(`gstNumber`)}
                                    errors={
                                       Boolean(touched.gstNumber) &&
                                       Boolean(errors.gstNumber)
                                    }
                                    message={errors.gstNumber}
                                 />
                              </div>

                              <div className="col-lg-4">
                                 <CustomFileUpload
                                    require
                                    label="GST Document"
                                    id="gstUpload"
                                    name="gstUpload"
                                    {...getFieldProps(`gstDocument`)}
                                    onChange={(e) => {
                                       setFieldValue("gstDocument", e.target.files?.[0]);
                                    }}
                                    errors={
                                       Boolean(touched.gstDocument) &&
                                       Boolean(errors.gstDocument)
                                    }
                                    message={errors.gstDocument}
                                 />
                              </div>

                              <div className="col-12 col-sm-12 col-md-12 col-lg- mb-3">
                                 <CustomTextArea
                                    label="Address"
                                    placeholder="Enter Address"
                                    name="address"
                                    {...getFieldProps(`address`)}
                                    errors={
                                       Boolean(touched.address) && Boolean(errors.address)
                                    }
                                    message={errors.address}
                                 />
                              </div>

                              <div className="col-12">
                                 <FieldArray
                                    name="contactPerson"
                                    render={(arrayHelpers) => (
                                       <div>
                                          {values.contactPerson.map((item, index) => {
                                             return (
                                                <div
                                                   key={index}
                                                   className={
                                                      index <
                                                         values.contactPerson.length -
                                                            1 &&
                                                      values.contactPerson.length > 1
                                                         ? "row border-bottom mb-4 pb-3"
                                                         : "row"
                                                   }
                                                >
                                                   <div className="col-12 col-sm-12 col-md- col-lg-4">
                                                      <CustomInput
                                                         require
                                                         label="Contact Person Name"
                                                         inputType="text"
                                                         id="#contact_person_name"
                                                         placeholder="Enter Contact Person Name."
                                                         {...getFieldProps(
                                                            `contactPerson[${index}].name`
                                                         )}
                                                         errors={
                                                            Boolean(
                                                               touched?.contactPerson?.[
                                                                  index
                                                               ]?.name
                                                            ) &&
                                                            Boolean(
                                                               errors?.contactPerson?.[
                                                                  index
                                                               ]?.name
                                                            )
                                                         }
                                                         message={
                                                            errors?.contactPerson?.[index]
                                                               ?.name
                                                         }
                                                      />
                                                   </div>
                                                   <div className="col-12 col-sm-12 col-md- col-lg-4">
                                                      <CustomInput
                                                         require
                                                         label="Mobile No"
                                                         inputType="number"
                                                         id="#mobile"
                                                         placeholder="Enter Contact Person Mobile No."
                                                         {...getFieldProps(
                                                            `contactPerson[${index}].mobile`
                                                         )}
                                                         errors={
                                                            Boolean(
                                                               touched?.contactPerson?.[
                                                                  index
                                                               ]?.mobile
                                                            ) &&
                                                            Boolean(
                                                               errors?.contactPerson?.[
                                                                  index
                                                               ]?.mobile
                                                            )
                                                         }
                                                         message={
                                                            errors?.contactPerson?.[index]
                                                               ?.mobile
                                                         }
                                                      />
                                                   </div>
                                                   <div className="col-12 col-sm-12 col-md- col-lg-4">
                                                      <CustomInput
                                                         require
                                                         label="Email Id"
                                                         inputType="text"
                                                         id="#email"
                                                         placeholder="Enter Contact Person Email"
                                                         {...getFieldProps(
                                                            `contactPerson[${index}].email`
                                                         )}
                                                         errors={
                                                            Boolean(
                                                               touched?.contactPerson?.[
                                                                  index
                                                               ]?.email
                                                            ) &&
                                                            Boolean(
                                                               errors?.contactPerson?.[
                                                                  index
                                                               ]?.email
                                                            )
                                                         }
                                                         message={
                                                            errors?.contactPerson?.[index]
                                                               ?.email
                                                         }
                                                      />
                                                   </div>

                                                   {index !== 0 && (
                                                      <div className="col-12 col-sm-12 col-md-12">
                                                         <button
                                                            type="button"
                                                            className="btn btn-danger float-right ml-3 mb-3"
                                                            onClick={() => {
                                                               arrayHelpers.remove(index);
                                                            }}
                                                         >
                                                            <i className="fas fa-trash"></i>{" "}
                                                            Delete Contact Person
                                                         </button>
                                                      </div>
                                                   )}
                                                </div>
                                             );
                                          })}
                                       </div>
                                    )}
                                 />

                                 <button
                                    type="button"
                                    className="btn btn-primary float-right"
                                    onClick={() => {
                                       setFieldValue("contactPerson", [
                                          ...values.contactPerson,
                                          { name: "", mobile: "", email: "" },
                                       ]);
                                    }}
                                 >
                                    <i className="fas fa-plus"></i> Add Contact Person
                                 </button>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>

               <div className="card card-primary">
                  <div className="card-header">
                     <h3 className="card-title">Bank Details</h3>
                  </div>
                  <div className="card-body">
                     <div className="row">
                        <div className="col-lg-4">
                           <CustomInput
                              require
                              label="Account Number"
                              id="account_no"
                              name="account_no"
                              placeholder="Enter Account Number"
                              {...getFieldProps(`accountNo`)}
                              errors={
                                 Boolean(touched.accountNo) && Boolean(errors.accountNo)
                              }
                              message={errors.accountNo}
                           />
                        </div>
                        <div className="col-lg-4">
                           <CustomInput
                              require
                              label="Bank name"
                              id="bank_name"
                              name="bank_name"
                              placeholder="Enter Bank Name"
                              {...getFieldProps(`bankName`)}
                              errors={
                                 Boolean(touched.bankName) && Boolean(errors.bankName)
                              }
                              message={errors.bankName}
                           />
                        </div>
                        <div className="col-lg-4">
                           <CustomInput
                              require
                              label="IFSC Code"
                              id="ifsc_code"
                              name="ifsc_code"
                              placeholder="Enter IFSC Code"
                              {...getFieldProps(`ifscCode`)}
                              errors={
                                 Boolean(touched.ifscCode) && Boolean(errors.ifscCode)
                              }
                              message={errors.ifscCode}
                           />
                        </div>

                        <div className="col-lg-4">
                           <CustomInput
                              require
                              label="Account Holder Name"
                              id="account_holder_name"
                              name="account_holder_name"
                              placeholder="Enter Account Holder name"
                              {...getFieldProps(`accountHolderName`)}
                              errors={
                                 Boolean(touched.accountHolderName) &&
                                 Boolean(errors.accountHolderName)
                              }
                              message={errors.accountHolderName}
                           />
                        </div>
                        <div className="col-lg-4">
                           <CustomFileUpload
                              require
                              label="Bank Document"
                              id="bankDetails"
                              name="bankDetails"
                              {...getFieldProps(`bankDocument`)}
                              onChange={(e) => {
                                 setFieldValue("bankDocument", e.target.files?.[0]);
                              }}
                              errors={
                                 Boolean(touched.bankDocument) &&
                                 Boolean(errors.bankDocument)
                              }
                              message={errors.bankDocument}
                           />
                        </div>
                     </div>
                  </div>
               </div>

               <div className="row">
                  <div className="col-lg-12">
                     <div className="card card-primary">
                        <div className="card-header">
                           <h3 className="card-title">Agreement Details</h3>
                        </div>
                        <div className="card-body row">
                           <div className="col-lg-12">
                              {/* <CustomFileUpload
                                 require
                                 label="Agreement Details"
                                 id="agreementDetails"
                                 {...getFieldProps(`agreementDocument`)}
                                 onChange={(e, file) => {
                                    setFieldValue("agreementDocument", file);
                                 }}
                                 // value={getFieldProps("agreementDocument").value}
                                 errors={
                                    Boolean(touched.agreementDocument) &&
                                    Boolean(errors.agreementDocument)
                                 }
                                 message={errors.agreementDocument}
                              /> */}

                              <FieldArray
                                 name="agreementDetails"
                                 render={(arrayHelpers) => (
                                    <div>
                                       {values?.agreementDetails?.map?.((item, index) => {
                                          return (
                                             <div className="row" key={index}>
                                                <div className="col-lg-4" key={index}>
                                                   <CustomFileUpload
                                                      require
                                                      label="Agreement Document"
                                                      {...getFieldProps(
                                                         `agreementDetails[${index}].agreementDocument`
                                                      )}
                                                      onChange={(event, file) =>
                                                         setFieldValue(
                                                            `agreementDetails[${index}].agreementDocument`,
                                                            file
                                                         )
                                                      }
                                                      errors={
                                                         Boolean(
                                                            touched.agreementDetails?.[
                                                               index
                                                            ]?.agreementDocument
                                                         ) &&
                                                         Boolean(
                                                            errors.agreementDetails?.[
                                                               index
                                                            ]?.agreementDocument
                                                         )
                                                      }
                                                      message={
                                                         errors.agreementDetails?.[index]
                                                            ?.agreementDocument
                                                      }
                                                   />
                                                </div>
                                                <div className="col-lg-3">
                                                   <CustomDatePicker
                                                      require
                                                      label="Start date"
                                                      placeholder="Start date"
                                                      {...getFieldProps(
                                                         `agreementDetails[${index}].startDate`
                                                      )}
                                                      errors={
                                                         Boolean(
                                                            touched.agreementDetails?.[
                                                               index
                                                            ]?.startDate
                                                         ) &&
                                                         Boolean(
                                                            errors.agreementDetails?.[
                                                               index
                                                            ]?.startDate
                                                         )
                                                      }
                                                      message={
                                                         errors.agreementDetails?.[index]
                                                            ?.startDate
                                                      }
                                                   />
                                                </div>
                                                <div className="col-lg-3">
                                                   <CustomDatePicker
                                                      require
                                                      label="End date"
                                                      placeholder="End date"
                                                      {...getFieldProps(
                                                         `agreementDetails[${index}].endDate`
                                                      )}
                                                      errors={
                                                         Boolean(
                                                            touched.agreementDetails?.[
                                                               index
                                                            ]?.endDate
                                                         ) &&
                                                         Boolean(
                                                            errors.agreementDetails?.[
                                                               index
                                                            ]?.endDate
                                                         )
                                                      }
                                                      message={
                                                         errors.agreementDetails?.[index]
                                                            ?.endDate
                                                      }
                                                   />
                                                </div>
                                                {values.agreementDetails.length > 1 && (
                                                   <div className="col-lg-2 mt-4 pt-2">
                                                      <button
                                                         type="button"
                                                         className="btn btn-danger"
                                                         onClick={() => {
                                                            arrayHelpers.remove(index);
                                                         }}
                                                      >
                                                         <i className="fas fa-trash"></i>{" "}
                                                         Delete
                                                      </button>
                                                   </div>
                                                )}
                                             </div>
                                          );
                                       })}
                                    </div>
                                 )}
                              />
                           </div>
                           <div className="my-2">
                              <button
                                 type="button"
                                 className="btn mx-1 btn-primary"
                                 onClick={() => {
                                    setFieldValue("agreementDetails", [
                                       ...values.agreementDetails,
                                       {
                                          startDate: "",
                                          endDate: "",
                                          agreementDocument: null,
                                       },
                                    ]);
                                 }}
                              >
                                 <i className="fas fa-plus"></i> Add Agreement Document
                              </button>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>

               <div className="mt-3 text-center">
                  <button
                     className="btn btn-primary px-4 py-3"
                     type="submit"
                     onClick={handleSubmit}
                  >
                     <h5 className="mb-0 text-uppercase">Submit</h5>
                  </button>
                  {/* <button className="btn btn-danger ml-3 px-4 py-3" type="submit">
                     <h5 className="mb-0 text-uppercase">reset</h5>
                  </button> */}
               </div>
            </Form>
         </FormikProvider>
      </div>
   );
};

export default ContractorMasterAdd;
